/** `sections/product-reviews.liquid` — `<product-reviews>`: renders Judge.me
 *  reviews fetched through the Wyld app's Shopify App Proxy, with pagination.
 *
 *  The proxy (`/apps/api/reviews`) holds Judge.me's private token and
 *  returns clean JSON: `{ count, page, per_page, reviews: [...] }`. Judge.me's
 *  public token is rejected by the JSON API, so the request must be proxied.
 *
 *  Note: review `name`/`body` are reviewer-supplied, so they are rendered with
 *  `textContent` only — never innerHTML.
 */

const MAX_PAGE_BUTTONS = 5

class ProductReviews extends HTMLElement {
  #abort
  #endpoint
  #productId
  #perPage
  #totalCount = 0
  #currentPage = 1

  connectedCallback() {
    this.#endpoint = this.dataset.endpoint
    this.#productId = this.dataset.productId
    this.#perPage = Number.parseInt(this.dataset.perPage, 10) || 10

    this.listEl = this.querySelector('[data-reviews-list]')
    this.statusEl = this.querySelector('[data-reviews-status]')
    this.paginationEl = this.querySelector('[data-reviews-pagination]')
    this.reviewTemplate = this.querySelector('[data-review-template]')
    this.starFilledTemplate = this.querySelector('[data-star-filled]')
    this.starEmptyTemplate = this.querySelector('[data-star-empty]')
    this.chevronTemplate = this.querySelector('[data-chevron]')

    if (!this.#endpoint || !this.#productId || !this.listEl) {
      this.hidden = true
      return
    }

    this.#abort = new AbortController()
    this.#loadPage(1).catch((error) => {
      if (error?.name === 'AbortError') return
      this.#showStatus('Reviews are unavailable right now.')
    })
  }

  disconnectedCallback() {
    this.#abort?.abort()
  }

  async #fetchPage(page) {
    const url = new URL(this.#endpoint, globalThis.location.origin)
    url.searchParams.set('product_id', this.#productId)
    url.searchParams.set('page', page)
    url.searchParams.set('per_page', this.#perPage)

    const response = await fetch(url, { signal: this.#abort.signal })
    if (!response.ok) throw new Error(`Reviews request failed: ${response.status}`)
    return response.json()
  }

  async #loadPage(page) {
    this.#currentPage = page
    this.#showStatus('Loading reviews\u2026')

    const data = await this.#fetchPage(page)
    this.#totalCount = Number.parseInt(data?.count, 10) || 0
    const reviews = Array.isArray(data?.reviews) ? data.reviews : []

    if (reviews.length === 0) {
      this.listEl.replaceChildren()
      this.#showStatus('No reviews yet.')
      this.paginationEl.hidden = true
      return
    }

    this.#hideStatus()
    this.#renderReviews(reviews)
    this.#renderPagination()
  }

  #renderReviews(reviews) {
    const fragment = document.createDocumentFragment()
    for (const review of reviews) {
      fragment.append(this.#buildReview(review))
    }
    this.listEl.replaceChildren(fragment)
  }

  #buildReview(review) {
    const node = this.reviewTemplate.content.firstElementChild.cloneNode(true)

    const name = this.#formatName(review?.name)
    node.querySelector('[data-name]').textContent = name || 'Anonymous'

    const location = (review?.location || '').trim()
    const locationEl = node.querySelector('[data-location]')
    if (location) {
      locationEl.textContent = location
      locationEl.hidden = false
    }

    if (review?.verified) {
      node.querySelector('[data-verified]').hidden = false
    }

    const rating = Math.max(0, Math.min(5, Math.round(Number(review?.rating) || 0)))
    this.#renderStars(node.querySelector('[data-stars]'), rating)

    const body = (review?.body || '').trim()
    node.querySelector('[data-body]').textContent = body ? `\u201C${body}\u201D` : ''

    node.querySelector('[data-date]').textContent = this.#formatDate(review?.created_at)

    return node
  }

  #renderStars(container, rating) {
    container.setAttribute('aria-label', `${rating} out of 5 stars`)
    const fragment = document.createDocumentFragment()
    for (let i = 1; i <= 5; i += 1) {
      const template = i <= rating ? this.starFilledTemplate : this.starEmptyTemplate
      fragment.append(template.content.firstElementChild.cloneNode(true))
    }
    container.replaceChildren(fragment)
  }

  #renderPagination() {
    const totalPages = Math.max(1, Math.ceil(this.#totalCount / this.#perPage))
    this.paginationEl.replaceChildren()
    if (totalPages <= 1) {
      this.paginationEl.hidden = true
      return
    }
    this.paginationEl.hidden = false

    this.paginationEl.append(this.#buildArrow('prev', this.#currentPage <= 1))
    for (const page of this.#pageWindow(totalPages)) {
      this.paginationEl.append(this.#buildPageButton(page))
    }
    this.paginationEl.append(this.#buildArrow('next', this.#currentPage >= totalPages))
  }

  // A sliding window of page numbers centered on the current page.
  #pageWindow(totalPages) {
    if (totalPages <= MAX_PAGE_BUTTONS) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    const half = Math.floor(MAX_PAGE_BUTTONS / 2)
    let start = this.#currentPage - half
    start = Math.max(1, Math.min(start, totalPages - MAX_PAGE_BUTTONS + 1))
    return Array.from({ length: MAX_PAGE_BUTTONS }, (_, i) => start + i)
  }

  #buildPageButton(page) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'product-reviews-page'
    button.textContent = String(page)
    if (page === this.#currentPage) {
      button.classList.add('is-active')
      button.setAttribute('aria-current', 'true')
    }
    button.addEventListener('click', () => this.#goToPage(page), { signal: this.#abort.signal })
    return button
  }

  #buildArrow(direction, disabled) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = `product-reviews-arrow product-reviews-arrow-${direction}`
    button.setAttribute('aria-label', direction === 'prev' ? 'Previous page' : 'Next page')
    button.disabled = disabled
    button.append(this.chevronTemplate.content.firstElementChild.cloneNode(true))
    button.addEventListener(
      'click',
      () => this.#goToPage(this.#currentPage + (direction === 'prev' ? -1 : 1)),
      { signal: this.#abort.signal }
    )
    return button
  }

  async #goToPage(page) {
    const totalPages = Math.max(1, Math.ceil(this.#totalCount / this.#perPage))
    const next = Math.max(1, Math.min(totalPages, page))
    if (next === this.#currentPage) return
    try {
      await this.#loadPage(next)
      this.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } catch (error) {
      if (error?.name === 'AbortError') return
      this.#showStatus('Reviews are unavailable right now.')
    }
  }

  // "Luke Waldner" -> "Luke W."; leaves single-word names unchanged.
  #formatName(value) {
    const parts = (value || '').trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return ''
    if (parts.length === 1) return parts[0]
    const first = parts[0]
    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase()
    return `${first} ${lastInitial}.`
  }

  #formatDate(value) {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    const yy = String(date.getFullYear()).slice(-2)
    return `${mm}/${dd}/${yy}`
  }

  #showStatus(message) {
    if (!this.statusEl) return
    this.statusEl.textContent = message
    this.statusEl.hidden = false
  }

  #hideStatus() {
    if (this.statusEl) this.statusEl.hidden = true
  }
}

if (!customElements.get('product-reviews')) {
  customElements.define('product-reviews', ProductReviews)
}
